import styled from '@emotion/styled';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { GlobalSearch } from '../../../data-contract/global-search';
import type { DropdownData } from '../../global-search/DropDown';
import { Dropdown } from '../../global-search/DropDown';
import { GlobalSearchInputV2 } from '../../global-search/GlobalSearchInputV2';
import type { TabParam } from '../../global-search/use-global-search-table-params';
import { useGlobalSearchTableParams } from '../../global-search/use-global-search-table-params';
import { useGlobalSearchQuery } from '../../query-hooks/global-search/global-search';
import { CustomThemeProvider } from '../../theming';

const mapSkillsToDropdown = (skills: GlobalSearch['skills']): readonly DropdownData[] => {
    return skills?.data.map((skill) => ({ id: skill.id, type: 'skill' as const })) ?? [];
};

const mapJobsToDropdown = (jobs: GlobalSearch['jobs']): readonly DropdownData[] => {
    return jobs?.data.map((job) => ({ id: job.id, type: 'job' as const })) ?? [];
};

const mapUsersToDropdown = (users: GlobalSearch['users']): readonly DropdownData[] => {
    return users?.data.map((user) => ({ id: user.id, type: 'user' as const })) ?? [];
};

const mapLearningOpportunitiesToDropdown = (
    learningOpportunities: GlobalSearch['learningOpportunities'],
): readonly DropdownData[] => {
    return (
        learningOpportunities?.data.map((learningOpportunity) => ({
            id: learningOpportunity.id,
            type: 'learning-opportunity' as const,
        })) ?? []
    );
};

const generateDropdownFlatList = (result: GlobalSearch | undefined): readonly DropdownData[] => {
    if (is.undefined(result)) {
        return [];
    }

    const { jobs, learningOpportunities, skills, users } = result;

    return [
        ...mapSkillsToDropdown(skills),
        ...mapJobsToDropdown(jobs),
        ...mapUsersToDropdown(users),
        ...mapLearningOpportunitiesToDropdown(learningOpportunities),
    ];
};

const SearchContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
    margin: `0 ${theme.spacing.l}`,
    maxWidth: '100%',
    padding: `${theme.spacing.l} 0`,
}));

export const GlobalSearchBarV2: FunctionComponent = () => {
    const {
        redirectToSearchTerm,
        redirectToSkillIds,
        searchTermResult: searchTermInitialValue,
        skillIds: skillIdsInitialValue,
    } = useGlobalSearchTableParams();

    const dropdownPositionRef = useRef<HTMLDivElement>(null);

    const [open, setDropdownOpen] = useState<boolean>(false);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState(searchTermInitialValue);

    const [skillIds, setSkillIds] = useState<ReadonlyArray<string>>(skillIdsInitialValue);
    const [currentCursor, setCurrentCursor] = useState<number>(-1);

    const { data: result, isFetching } = useGlobalSearchQuery(dropdownSearchTerm, skillIds);

    const dropDownData = useMemo(() => generateDropdownFlatList(result), [result]);

    useEffect(() => {
        const updatedCursorIndex = is.nonEmptyArray(dropDownData) ? 0 : -1;
        setCurrentCursor(updatedCursorIndex);
    }, [dropDownData]);

    const onCloseDropdown = (): void => setDropdownOpen(false);
    const onOpenDropdown = (): void => setDropdownOpen(true);

    const getActiveDropdownElement = (): DropdownData | undefined => {
        if (currentCursor <= dropDownData.length - 1) {
            const active = dropDownData[currentCursor];
            if (!is.undefined(active)) {
                return active;
            }
        }

        return;
    };

    const handleGlobalSearch = (sectionTab?: TabParam): void => {
        if (is.nonEmptyArray(skillIds)) {
            redirectToSkillIds(skillIds, sectionTab);
        } else {
            redirectToSearchTerm(dropdownSearchTerm, sectionTab);
        }
        setDropdownSearchTerm('');
        onCloseDropdown();
    };

    const addSkillToSearch = (skillId: string): void => {
        setSkillIds([...skillIds, skillId]);
    };

    return (
        <ClickAwayListener onClickAway={onCloseDropdown}>
            <SearchContainer ref={dropdownPositionRef}>
                <GlobalSearchInputV2
                    currentCursor={currentCursor}
                    dropDownData={dropDownData}
                    getActiveDropdownElement={getActiveDropdownElement}
                    handleGlobalSearch={handleGlobalSearch}
                    searchTerm={dropdownSearchTerm}
                    setCurrentCursor={setCurrentCursor}
                    setSearchTerm={setDropdownSearchTerm}
                    setSkillIds={setSkillIds}
                    skillIds={skillIds}
                    onCloseDropdown={onCloseDropdown}
                    onOpenDropdown={onOpenDropdown}
                />
                <CustomThemeProvider>
                    <Dropdown
                        activeId={getActiveDropdownElement()?.id ?? ''}
                        addSkillToSearch={addSkillToSearch}
                        handleGlobalSearch={handleGlobalSearch}
                        isFetching={isFetching}
                        open={open}
                        positionRef={dropdownPositionRef}
                        result={result}
                        searchTerm={dropdownSearchTerm}
                        setSearchTerm={setDropdownSearchTerm}
                        onCloseDropdown={onCloseDropdown}
                    />
                </CustomThemeProvider>
            </SearchContainer>
        </ClickAwayListener>
    );
};
